<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  // 'create' = formulario Crear Pedido | 'send' = seccion Enviar Pedido
  // 'factura' = panel Factura Electronica (DIAN via Odoo, factura.html)
  mode: {
    type: String,
    default: 'create',
  },
});

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
// ── Ferkos (cuenta 180): el mismo acordeón carga el panel propio de Ferkos (backend :5500 vía /ferkos-panel) ──
const FERKOS_ACCOUNT_ID = 180;
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFerkos = computed(() => Number(currentAccountId.value) === FERKOS_ACCOUNT_ID);

// Datos del contacto con los que se abrió el panel de ESTE chat (2026-10-25). Si cambian con el panel abierto —p. ej. el
// cliente comparte su número con el botón de WhatsApp (woocommerce-dashboard-app/numero-cliente.js)—, el iframe NO se
// recarga (la asesora perdía lo que llevaba escrito): el cambio le llega al panel por postMessage
// ('wc-contacto-actualizado'). Mientras el contacto no ha cargado, la URL se sigue actualizando como antes.
// Solo floristerías (cuenta 2): las otras empresas siguen exactamente como antes (regla: empresas independientes).
const CUENTA_FLORISTERIAS = 2;
const fijarContacto = computed(() => Number(currentAccountId.value) === CUENTA_FLORISTERIAS);
const frame = ref(null);
const chatActual = computed(() => props.conversationId || currentChat.value?.id || '');
const datosUrl = ref({ chat: null, cargado: false, phone: '', name: '', email: '' });
let avisado = null;
watch(
  () => [chatActual.value, contact.value?.id, contact.value?.phone_number || '', contact.value?.name || '', contact.value?.email || ''],
  ([chat, id, phone, name, email]) => {
    const u = datosUrl.value;
    if (u.chat !== chat || !u.cargado) {
      datosUrl.value = { chat, cargado: !!id, phone, name, email };
      avisado = null;
      return;
    }
    if (!fijarContacto.value) return;
    const antes = avisado || u;
    if (phone === antes.phone && name === antes.name && email === antes.email) return;
    avisado = { phone, name, email };
    frame.value?.contentWindow?.postMessage({ type: 'wc-contacto-actualizado', phone, name, email }, window.location.origin);
  },
  { immediate: true }
);

const iframeSrc = computed(() => {
  const inboxId = currentChat.value?.inbox_id || '';
  const { phone, name, email } = fijarContacto.value
    ? datosUrl.value
    : { phone: contact.value?.phone_number || '', name: contact.value?.name || '', email: contact.value?.email || '' };
  const conversationId = chatActual.value;
  const params = new URLSearchParams({
    inbox_id: inboxId,
    phone: phone,
    name: name,
    email: email,
    conversation_id: conversationId,
    mode: props.mode,
  });
  // Pass GCLID/UTM attribution data from CONVERSATION custom_attributes (not contact)
  // Attribution is stored per-conversation to avoid cross-store overwrites
  const wcAttr = currentChat.value?.custom_attributes?.wc_attribution;
  if (wcAttr && typeof wcAttr === 'object') {
    params.set('attribution', JSON.stringify(wcAttr));
  }
  if (isFerkos.value) {
    return '/ferkos-panel/index.html?_v=1&' + params.toString();
  }
  const page = props.mode === 'factura' ? 'factura.html' : 'index.html';
  return '/wc-dashboard/' + page + '?_v=6&' + params.toString();
});

function onIframeMessage(e) {
  if (e.data && e.data.type === 'wc-order-created') {
    const convId = currentChat.value?.id;
    if (!convId) return;
    const current = store.getters['conversationLabels/getConversationLabels'](convId);
    if (!current.includes('agendando')) {
      store.dispatch('conversationLabels/update', {
        conversationId: convId,
        labels: [...current, 'agendando'],
      });
    }
  }
}

onMounted(() => {
  window.addEventListener('message', onIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', onIframeMessage);
});
</script>

<template>
  <div class="wc-dashboard-wrap">
    <iframe
      ref="frame"
      :src="iframeSrc"
      class="wc-dashboard-frame"
      :style="{ height: props.mode === 'send' ? '45vh' : props.mode === 'factura' ? '75vh' : '70vh' }"
    />
  </div>
</template>

<style scoped>
.wc-dashboard-wrap {
  width: 100%;
}
.wc-dashboard-frame {
  width: 100%;
  height: 70vh;
  border: 0;
}
</style>
